import { Component, ViewChild } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from '../../core/user/user.service';
import { User } from '../../core/user/user.types';
import { AsmService } from '../../services/asm/asm.service';
import { ScanStatusResponse } from '../../interfaces/asm/ScanStatusResponse';
import { ScanHistory, VulnerabilitySeverity, VulnerabilitySeverityMap } from '../../interfaces/asm/models';
import { HomeComponent } from './home/home.component';
import { SubdomainsComponent } from './subdomains/subdomains.component';
import { VisualizationComponent } from './visualization/visualization.component';
import { QuerySubdomainsResponse } from '../../interfaces/asm/QuerySubdomainsResponse';
import { QueryEndpointsResponse } from '../../interfaces/asm/QueryEndpointsResponse';
import { QueryVulnerabilitiesResponse } from '../../interfaces/asm/QueryVulnerabilitiesResponse';

@Component({
    selector: 'app-a-scan-findings',
    standalone: true,
    imports: [
        MatCardModule,
        MatTabsModule,
        TranslocoModule,
        MatProgressSpinnerModule,
        HomeComponent,
        SubdomainsComponent,
        VisualizationComponent,
    ],
    templateUrl: './a-scan-findings.component.html',
    styleUrl: './a-scan-findings.component.scss',
})
export class AScanFindingsComponent {
    user: User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    // scanId
    scanId: string;

    isLoadingResults = true;
    isRateLimitReached = false;
    // isToggled
    isToggled = false;

    @ViewChild('home', { static: false }) home: HomeComponent;
    @ViewChild('subdomains', { static: false }) subdomains: SubdomainsComponent;
    @ViewChild('visualization', { static: false })
    visualization: VisualizationComponent;

    constructor(
        public themeService: CustomizerSettingsService,
        private _userService: UserService,
        private asmService: AsmService,
        private route: ActivatedRoute
    ) {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.route.params.subscribe((params) => {
            this.scanId = params['scanId'];
            // Subscribe to the user service
            this._userService.user$
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((user: User) => {
                    this.user = user;
                    this.asmService
                        .getScanStatus(this.user.slug)
                        .subscribe((response: ScanStatusResponse) => {
                            this.isLoadingResults = false;
                            let scans: ScanHistory[] = [];
                            scans.push(...response.scans.pending);
                            scans.push(...response.scans.scanning);
                            scans.push(...response.scans.completed);
                            let scan = scans.find(
                                ({ id }) => id.toString() === this.scanId
                            );
                            if (scan) {
                                this.home.scan = scan;
                                this.home.changeDataScan();
                                this.visualization.scan = scan;
                                this.visualization.changeData();
                                this.asmService
                                    .getQuerySubdomains(
                                        this.user.slug,
                                        this.home.scan.id.toString()
                                    )
                                    .subscribe(
                                        (response: QuerySubdomainsResponse) => {
                                            this.home.listSubdomains =
                                                response.subdomains;
                                            this.home.changeDataSubdomains();
                                            this.subdomains.data =
                                                response.subdomains;
                                            this.subdomains.changeData();
                                        }
                                    );
                                this.asmService
                                    .getQueryEndpoints(
                                        this.home.scan.id.toString()
                                    )
                                    .subscribe(
                                        (response: QueryEndpointsResponse) => {
                                            this.home.listEndpoints =
                                                response.endpoints;
                                            this.home.changeDataEndPoints();
                                        }
                                    );
                                this.asmService
                                    .getQueryVulnerabilities(this.user.slug)
                                    .subscribe(
                                        (
                                            response: QueryVulnerabilitiesResponse
                                        ) => {
                                            this.home.listVulnerabilities =
                                                response.vulnerabilities.sort(
                                                    (a, b) => {
                                                        return (
                                                            this.severityCode(
                                                                b.severity.toString()
                                                            ) -
                                                            this.severityCode(
                                                                a.severity.toString()
                                                            )
                                                        );
                                                    }
                                                );
                                            this.home.changeDataVulnerabilities();
                                        }
                                    );
                            }
                        });
                });
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // severityCode
    severityCode(severity: String): number {
        switch (severity) {
            case VulnerabilitySeverityMap[VulnerabilitySeverity.INFO]:
                return VulnerabilitySeverity.INFO;
            case VulnerabilitySeverityMap[VulnerabilitySeverity.LOW]:
                return VulnerabilitySeverity.LOW;
            case VulnerabilitySeverityMap[VulnerabilitySeverity.MEDIUM]:
                return VulnerabilitySeverity.MEDIUM;
            case VulnerabilitySeverityMap[VulnerabilitySeverity.HIGH]:
                return VulnerabilitySeverity.HIGH;
            case VulnerabilitySeverityMap[VulnerabilitySeverity.CRITICAL]:
                return VulnerabilitySeverity.CRITICAL;
            default:
                return VulnerabilitySeverity.UNKNOWN;
        }
    }
}
